import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * A panel anchored under a toolbar button.
 *
 * Portalled to <body> and positioned from the anchor's rect. Rendering it in
 * place looked right on a desktop and was invisible on a phone: the mobile
 * toolbar scrolls horizontally, and `overflow-x: auto` clips anything
 * absolutely positioned inside it — so the Aa button opened a panel that could
 * never be seen.
 */
export function Popover({ anchor, className = '', onClose, children }: {
  anchor: HTMLElement | null
  className?: string
  onClose: () => void
  children: React.ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!anchor || !el) return
    const a = anchor.getBoundingClientRect()
    const r = el.getBoundingClientRect()
    const gap = 6
    // Keep it on screen: a button near the right edge would otherwise open a
    // panel that runs off it.
    const left = Math.max(8, Math.min(a.left, window.innerWidth - r.width - 8))
    const below = a.bottom + gap
    const top = below + r.height > window.innerHeight - 8
      ? Math.max(8, a.top - r.height - gap)
      : below
    setPos({ left, top })
  }, [anchor])

  return createPortal(
    <>
      <div className="popover-scrim" onMouseDown={onClose} onTouchStart={onClose} />
      <div
        ref={ref}
        className={`popover ${className}`}
        style={{ position: 'fixed', left: pos?.left ?? -9999, top: pos?.top ?? -9999, visibility: pos ? 'visible' : 'hidden' }}
      >
        {children}
      </div>
    </>,
    document.body,
  )
}
